import { create } from 'zustand'

import {
  MaxChatMessage,
  MaxRegisterTurnInput,
  MaxTurnFinalContent,
  MaxTurnState,
  MaxTurnStreamMetadata,
} from '../types'

/**
 * In-memory state of every turn the browser sent, keyed by `clientRequestId` (V2-D02).
 * Never persisted; it survives conversation switches and is cleared on disconnect.
 */

type MaxTurnStoreState = {
  turns: Record<string, MaxTurnState>
}

type TurnId = string | null | undefined

type MaxTurnStoreActions = {
  register: (input: MaxRegisterTurnInput) => MaxTurnState
  get: (clientRequestId: TurnId) => MaxTurnState | undefined
  setUserMessage: (clientRequestId: TurnId, message: MaxChatMessage) => void
  bindRequest: (clientRequestId: TurnId, requestId: string) => void
  bindConversation: (clientRequestId: TurnId, conversationId: string) => void
  /** Stores the id without the `MESSAGE#` prefix. */
  setAssistantMessageId: (clientRequestId: TurnId, messageId: string) => void
  setStreamMetadata: (clientRequestId: TurnId, metadata: MaxTurnStreamMetadata) => void
  setVisibleContent: (clientRequestId: TurnId, content: string) => void
  markStreamEnded: (clientRequestId: TurnId) => void
  markFinal: (clientRequestId: TurnId, final: MaxTurnFinalContent) => void
  remove: (clientRequestId: TurnId) => void
  /** Turns of that tenant and conversation, oldest first. */
  forConversation: (tenantId: string, conversationId: string | null) => MaxTurnState[]
  clearAll: () => void
}

type MaxTurnStore = MaxTurnStoreState & MaxTurnStoreActions

const cleanMessageId = (messageId: string | null | undefined): string | null => {
  if (!messageId) return null
  const cleaned = messageId.replace(/^MESSAGE#/, '').trim()
  return cleaned || null
}

const useMaxTurnStore = create<MaxTurnStore>((set, get) => {
  const updateTurn = (
    clientRequestId: TurnId,
    updates: Partial<Omit<MaxTurnState, 'clientRequestId'>>
  ): void => {
    if (!clientRequestId) return

    set((state) => {
      const turn = state.turns[clientRequestId]
      if (!turn) return state

      return {
        turns: {
          ...state.turns,
          [clientRequestId]: { ...turn, ...updates },
        },
      }
    })
  }

  return {
    turns: {},

    register: (input) => {
      const turn: MaxTurnState = {
        clientRequestId: input.clientRequestId,
        tenantId: input.tenantId,
        conversationId: input.conversationId ?? null,
        tempConversationId: input.tempConversationId ?? null,
        requestId: null,
        assistantMessageId: null,
        userMessage: null,
        startedAt: Date.now(),
        streamMetadata: {},
        visibleContent: '',
        streamEnded: false,
        final: null,
      }

      set((state) => ({
        turns: {
          ...state.turns,
          [turn.clientRequestId]: turn,
        },
      }))

      return turn
    },

    get: (clientRequestId) => {
      if (!clientRequestId) return undefined
      return get().turns[clientRequestId]
    },

    setUserMessage: (clientRequestId, message) =>
      updateTurn(clientRequestId, { userMessage: message }),

    bindRequest: (clientRequestId, requestId) => updateTurn(clientRequestId, { requestId }),

    bindConversation: (clientRequestId, conversationId) =>
      updateTurn(clientRequestId, { conversationId }),

    setAssistantMessageId: (clientRequestId, messageId) =>
      updateTurn(clientRequestId, { assistantMessageId: cleanMessageId(messageId) }),

    setStreamMetadata: (clientRequestId, metadata) =>
      updateTurn(clientRequestId, { streamMetadata: { ...metadata } }),

    setVisibleContent: (clientRequestId, content) =>
      updateTurn(clientRequestId, { visibleContent: content }),

    markStreamEnded: (clientRequestId) => updateTurn(clientRequestId, { streamEnded: true }),

    markFinal: (clientRequestId, final) => updateTurn(clientRequestId, { final }),

    remove: (clientRequestId) => {
      if (!clientRequestId) return

      set((state) => {
        if (!state.turns[clientRequestId]) return state

        const nextTurns = { ...state.turns }
        delete nextTurns[clientRequestId]
        return { turns: nextTurns }
      })
    },

    forConversation: (tenantId, conversationId) =>
      Object.values(get().turns)
        .filter((turn) => turn.tenantId === tenantId && turn.conversationId === conversationId)
        .sort((left, right) => left.startedAt - right.startedAt),

    clearAll: () => set({ turns: {} }),
  }
})

export default useMaxTurnStore
